import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { EnrichedProfile } from "@/lib/supabase/types";
import { cn, getProfileLink } from "@/lib/utils";
import Link from "next/link";

interface UserCardProps {
	profile: EnrichedProfile | null;
	showAvatar?: boolean;
	showName?: boolean;
	showDescription?: boolean;
	variant?: "ghost" | "link" | "default";
	onMouseEnter?: () => void;
}

export function UserCard({
	profile,
	showDescription,
	showAvatar,
	showName = true,
	variant = "default",
	onMouseEnter,
}: UserCardProps) {
	const variantClass = () => {
		switch (variant) {
			case "ghost":
				return "";
			case "link":
				return "";
			default:
				return cn(
					"text-md text-primary text-nowrap flex flex-row gap-1 h-full items-center rounded-full",
					"hover:bg-primary/5 border border-transparent hover:border-primary/20",
					"transition-all duration-200 ease-in-out",
				);
		}
	};

	const preUsername = () => {
		switch (variant) {
			case "link":
				return "@";
			default:
				return "";
		}
	};

	const variantUsername = () => {
		switch (variant) {
			case "link":
				return "text-blue-500 hover:underline hover:underline-offset-4 cursor-pointer";
			default:
				return "text-primary font-bold px-1";
		}
	};

	return (
		<Link
			href={getProfileLink(profile?.username || profile?.id || "")}
			className={variantClass()}
			onMouseEnter={onMouseEnter}
		>
			{showAvatar && (
				<Avatar>
					<AvatarImage
						src={profile?.avatar_url || "/images/logo.png"}
						alt={profile?.name || ""}
						className={cn(
							"size-10 rounded-full transition-all border border-primary/20",
							"hover:border-primary hover:scale-102",
						)}
					/>
					<AvatarFallback>ZM</AvatarFallback>
				</Avatar>
			)}

			{showName && (
				<span className={variantUsername()}>
					{preUsername()}
					{profile?.username || profile?.name}
				</span>
			)}

			{showDescription && profile?.description && (
				<p className="text-xs text-nowrap px-1">{profile?.description}</p>
			)}
		</Link>
	);
}
